import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, MailOutline } from '@mui/icons-material';
import CopyAllIcon from '@mui/icons-material/CopyAll';

import {
  ILegalTransactionMeetingEmail,
  legalTransactionMeetingEmailSchema,
} from '../../../Interfaces/ILegalTransactionMeetingEmail';
import { GenericMeetingEmailEdit } from '../../../MeetingEmail/GenericMeetingEmailEdit';
import { GenericMeetingEmailImport } from '../../../MeetingEmail/GenericMeetingEmailImport';
import { LegalTransactionMeetingEmailRow } from './LegalTransactionMeetingEmailRow';

interface Props {
  idLegalTransaction: number;
  idLegalTransactionType: number;
  idLegalTransactionMeeting: number;
  meetingEmailArray: ILegalTransactionMeetingEmail[];
  setMeetingEmailArray: (arr: ILegalTransactionMeetingEmail[]) => void;
}

export const LegalTransactionMeetingEmailOverview: React.FC<Props> = ({
  idLegalTransaction,
  idLegalTransactionType,
  idLegalTransactionMeeting,
  meetingEmailArray,
  setMeetingEmailArray,
}) => {
  const [editingEmail, setEditingEmail] =
    useState<ILegalTransactionMeetingEmail | null>(null);
  const [openImport, setOpenImport] = useState(false);
  const tempIdRef = useRef(0);

  // Factory für neue Objekte mit eindeutiger negativer ID
  const createBlank = (): ILegalTransactionMeetingEmail => {
    const tempId = --tempIdRef.current;
    return legalTransactionMeetingEmailSchema.parse({
      idLegalTransactionMeetingEmail: tempId,
      idLegalTransactionMeeting,
    });
  };

  const handleSave = (email: ILegalTransactionMeetingEmail) => {
    const exists = meetingEmailArray.some(
      e =>
        e.idLegalTransactionMeetingEmail ===
        email.idLegalTransactionMeetingEmail
    );
    if (exists) {
      setMeetingEmailArray(
        meetingEmailArray.map(e =>
          e.idLegalTransactionMeetingEmail ===
          email.idLegalTransactionMeetingEmail
            ? email
            : e
        )
      );
    } else {
      setMeetingEmailArray([...meetingEmailArray, email]);
    }
    setEditingEmail(null);
  };

  const handleDelete = (email: ILegalTransactionMeetingEmail) => {
    setMeetingEmailArray(
      meetingEmailArray.filter(
        e =>
          e.idLegalTransactionMeetingEmail !==
          email.idLegalTransactionMeetingEmail
      )
    );
    setEditingEmail(null);
  };

  // Hier werden importierte E-Mails mit neuen Temp-IDs versehen
  const handleImport = (imported: ILegalTransactionMeetingEmail[]) => {
    setMeetingEmailArray([...meetingEmailArray, ...imported]);
    setOpenImport(false);
  };

  const hasEmails = meetingEmailArray.length > 0;

  return (
    <>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={hasEmails ? 1 : 2}>
        <Typography variant='subtitle1' fontWeight={500}>
          Termin-E-Mails
        </Typography>
        <Stack direction='row' spacing={0.5}>
          <Tooltip title='Vorlage importieren'>
            <IconButton size='small' onClick={() => setOpenImport(true)}>
              <CopyAllIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Neue E-Mail hinzufügen'>
            <IconButton size='small' onClick={() => setEditingEmail(createBlank())}>
              <Add fontSize='small' />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {!hasEmails ? (
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          py={3}
          sx={{ color: 'text.disabled' }}
        >
          <MailOutline sx={{ fontSize: 36, mb: 1, opacity: 0.4 }} />
          <Typography variant='body2' color='text.disabled'>
            Noch keine Termin-E-Mails angelegt
          </Typography>
          <Typography variant='caption' color='text.disabled' sx={{ mt: 0.5 }}>
            Über das + oben rechts kann eine E-Mail hinzugefügt werden.
          </Typography>
        </Box>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Titel</TableCell>
              <TableCell>Erstellung</TableCell>
              <TableCell>Änderung</TableCell>
              <TableCell>Absage</TableCell>
              <TableCell>± Tage</TableCell>
              <TableCell>Kalender</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {meetingEmailArray.map(email => (
              <LegalTransactionMeetingEmailRow
                key={email.idLegalTransactionMeetingEmail}
                idLegalTransactionMeeting={idLegalTransactionMeeting}
                meetingEmailObject={email}
                meetingEmailArray={meetingEmailArray}
                setMeetingEmailArray={setMeetingEmailArray}
                onEdit={() => setEditingEmail(email)}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {editingEmail && (
        <GenericMeetingEmailEdit
          open
          template={editingEmail}
          onSave={handleSave}
          onCancel={() => setEditingEmail(null)}
          onDelete={() => editingEmail && handleDelete(editingEmail)}
        />
      )}

      {openImport && (
        <GenericMeetingEmailImport
          open={openImport}
          onClose={() => setOpenImport(false)}
          mode='meeting'
          idLegalTransaction={idLegalTransaction}
          idLegalTransactionType={idLegalTransactionType}
          idLegalTransactionMeeting={idLegalTransactionMeeting}
          existingMeetingEmails={meetingEmailArray}
          onImportMeetingEmails={handleImport}
        />
      )}
    </>
  );
};
